import RoomIcon from '@material-ui/icons/Room'
import React from 'react'
import { Relative } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { cyanD1, dividerGray, lightGray1, midGray } from 'shared/theme/colors'
import { centeredContent } from 'shared/theme/mixins'
import styled from 'styled-components'

import { Box } from '../../../components/Box/Box'
import { isTest } from '../../../config/config'
import { LoadStop, LoadStopTypeDetail } from '../../../models/load'
import { MultistopPathItem } from './MultistopPathItem'

interface MultistopPathProps {
  deadhead?: number
  stops: Array<LoadStop>
  maskData?: boolean
  openMap?: Handler
  isSmall?: boolean
  isMedium?: boolean
  showAllStopsOnSmall?: boolean
}

export function MultistopPathTimeline({
  deadhead,
  stops,
  maskData,
  openMap,
  isSmall,
  isMedium,
  showAllStopsOnSmall,
}: MultistopPathProps) {
  const [origStop, ...restStops] = stops
  const destStop = restStops[restStops.length - 1]
  const middleStops = restStops.slice(0, -1)
  const allStopsHaveLocation = stops.every((s) => s.locationAddress1 || s.locationCity)
  const showGetDirections = allStopsHaveLocation && openMap && !maskData && !isSmall

  function renderMiddleStops(stops: LoadStop[]) {
    if (stops.length === 0) return null
    if (showAllStopsOnSmall || !isSmall) {
      return stops.map((load) => renderStop(load))
    }

    return (
      <React.Fragment>
        <Relative>
          <MultiStopDot $isSmall={isSmall} $isMedium={isMedium} />
          <TimeLineDash $isSmall={isSmall} $isMedium={isMedium} $isMiddleStop />
        </Relative>
        <Text variant="header3" mt={-0.7}>
          {stops.length} stop{stops.length > 1 ? 's' : ''}
        </Text>
      </React.Fragment>
    )
  }

  function renderStop(stop: LoadStop, showTimeline = true) {
    return (
      <React.Fragment key={stop.sequenceNumber}>
        <Relative>
          <Box mt={isSmall ? 0.6 : -0.4} />
          <MultiStopDot
            $isPickup={stop.typeDetail === LoadStopTypeDetail.PICKUP}
            $isSmall={isSmall}
            $isMedium={isMedium}
          >
            {stop.sequenceNumber}
          </MultiStopDot>
          {showTimeline && <TimeLineDash $isSmall={isSmall} $isMedium={isMedium} />}
        </Relative>

        <MultistopPathItem stop={stop} maskData={maskData} isSmall={isSmall} isMedium={isMedium} />
      </React.Fragment>
    )
  }

  return (
    <>
      <TimeLine $isSmall={isSmall} $isMedium={isMedium}>
        {!!deadhead && (
          <>
            <Relative>
              <Dash />
              <TimeLineDash $isSmall={isSmall} $isMedium={isMedium} $isDeadHeaad />
            </Relative>
            <div>
              <Text variant="paragraph2" color={lightGray1} mt={-0.8}>
                D/H {deadhead} mi
              </Text>
            </div>
          </>
        )}

        {renderStop(origStop)}
        {renderMiddleStops(middleStops)}
        {renderStop(destStop, false)}
      </TimeLine>
      {showGetDirections ? (
        <Button variant="text" onClick={openMap} margin="20px 0 0" icon={<RoomIcon color="primary" />}>
          Get directions
        </Button>
      ) : isTest ? (
        <Button variant="text" onClick={() => ''} margin="20px 0 0" icon={<RoomIcon color="primary" />}>
          Get directions
        </Button>
      ) : null}
    </>
  )
}

function getDotSize(isSmall?: boolean, isMedium?: boolean) {
  if (isMedium) return '12px'
  if (isSmall) return '8px'
  return '24px'
}

const TimeLine = styled.div<{ $gridGap?: string; $isSmall?: boolean; $isMedium?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $isSmall, $isMedium }) => getDotSize($isSmall, $isMedium)} 1fr;
  grid-gap: ${({ $isSmall, $isMedium }) => ($isSmall && !$isMedium ? '10px' : '20px')};
  grid-gap: ${({ $isMedium }) => $isMedium && '20px 10px'};
`

const Dash = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 2px;
  width: 12px;
  border-top: 2px solid ${dividerGray};
`

export const MultiStopDot = styled.div<{ $isPickup?: boolean; $isSmall?: boolean; $isMedium?: boolean }>`
  background-color: ${({ $isPickup }) => ($isPickup ? midGray : cyanD1)};
  width: ${({ $isSmall, $isMedium }) => getDotSize($isSmall, $isMedium)};
  min-width: ${({ $isSmall, $isMedium }) => getDotSize($isSmall, $isMedium)};
  height: ${({ $isSmall, $isMedium }) => getDotSize($isSmall, $isMedium)};
  min-height: ${({ $isSmall, $isMedium }) => getDotSize($isSmall, $isMedium)};
  padding-top: ${({ $isSmall }) => ($isSmall ? '1px' : '2px')};
  border-radius: 50%;
  color: white;
  font-weight: 600;
  font-size: ${({ $isSmall }) => ($isSmall ? '0' : '13px')};
  position: relative;
  z-index: 1;
  ${centeredContent};
`

export const TimeLineDash = styled.div<{
  $isSmall?: boolean
  $isMedium?: boolean
  $isDeadHeaad?: boolean
  $isMiddleStop?: boolean
}>`
  background-color: ${dividerGray};
  position: absolute;
  z-index: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  padding-top: 2px;
  top: 10px;
  bottom: -25px;

  ${({ $isSmall, $isMedium }) => $isSmall && !$isMedium && 'bottom: -15px;'};
  ${({ $isDeadHeaad }) => $isDeadHeaad && 'top: 2px;'};
  ${({ $isMiddleStop }) => $isMiddleStop && 'top: 6px;'};
`
